import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  Users, Search, Plus, Star, Phone, Mail, Calendar, ShoppingBag,
  TrendingUp, X, Edit2, Trash2, ChevronLeft, ChevronRight, RefreshCw,
  Award, Clock,
} from 'lucide-react';
import { api } from '../../utils/api';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Customer {
  id: number;
  name: string;
  phone?: string;
  email?: string;
  birthday?: string;
  notes?: string;
  tier: 'bronce' | 'plata' | 'oro' | 'diamante';
  loyalty_points?: number;
  visit_count: number;
  total_spent: number;
  last_visit_at?: string;
  created_at: string;
  identification_type?: string;
  identification_number?: string;
  verification_digit?: string;
  organization_type?: string;
  fiscal_responsibility?: string;
  address?: string;
  city_code?: string;
  city_name?: string;
}

interface CustomerDetail {
  customer: Customer;
  orders: any[];
  top_products: { name: string; qty: number; total: number }[];
  by_month: { month: string; visits: number; spent: number }[];
}

interface GlobalStats {
  totals: { total_customers: number; total_revenue: number; avg_spent: number; total_visits: number };
  by_tier: { tier: string; count: number; revenue: number }[];
  new_this_month: number;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const TIER_META = {
  bronce:   { label: 'Bronce',   emoji: '🥉', color: 'bg-amber-700/10 text-amber-700 border-amber-200',  ring: 'ring-amber-300'   },
  plata:    { label: 'Plata',    emoji: '🥈', color: 'bg-gray-400/10  text-gray-600  border-gray-200',   ring: 'ring-gray-300'    },
  oro:      { label: 'Oro',      emoji: '🥇', color: 'bg-yellow-400/10 text-yellow-700 border-yellow-200', ring: 'ring-yellow-300' },
  diamante: { label: 'Diamante', emoji: '💎', color: 'bg-violet-400/10 text-violet-700 border-violet-200', ring: 'ring-violet-300' },
} as const;

function fmt(n: number) {
  return '$ ' + Math.round(n).toLocaleString('es-CO');
}

function fmtDate(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
}

function timeAgo(iso?: string) {
  if (!iso) return '—';
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (d === 0) return 'Hoy';
  if (d === 1) return 'Ayer';
  if (d < 30)  return `Hace ${d} días`;
  if (d < 365) return `Hace ${Math.floor(d / 30)} meses`;
  return `Hace ${Math.floor(d / 365)} años`;
}

// ── Customer Form Modal ───────────────────────────────────────────────────────

function CustomerFormModal({ customer, onClose, onSaved }: {
  customer?: Customer | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name:     customer?.name     ?? '',
    phone:    customer?.phone    ?? '',
    email:    customer?.email    ?? '',
    birthday: customer?.birthday ?? '',
    notes:    customer?.notes    ?? '',
    identification_type: customer?.identification_type ?? '13',
    identification_number: customer?.identification_number ?? '',
    verification_digit: customer?.verification_digit ?? '',
    organization_type: customer?.organization_type ?? '2',
    fiscal_responsibility: customer?.fiscal_responsibility ?? 'R-99-PN',
    address: customer?.address ?? '',
    city_code: customer?.city_code ?? '11001',
    city_name: customer?.city_name ?? 'Bogotá',
  });
  const [saving, setSaving] = useState(false);

  const set = (k: keyof typeof form, v: string) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('El nombre es requerido'); return; }
    setSaving(true);
    try {
      if (customer) {
        await api.put(`/customers/${customer.id}`, form);
        toast.success('Cliente actualizado');
      } else {
        await api.post('/customers', form);
        toast.success('Cliente creado');
      }
      onSaved();
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ label, id, type = 'text', value, onChange, placeholder = '' }: any) => (
    <div>
      <label htmlFor={id} className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">{label}</label>
      <input
        id={id} type={type} value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/60"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-neutral-800 shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-neutral-700 px-5 py-4">
          <h2 className="font-semibold text-gray-900 dark:text-white">
            {customer ? 'Editar cliente' : 'Nuevo cliente'}
          </h2>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-neutral-700">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <Field id="name"     label="Nombre *"       value={form.name}     onChange={(v: string) => set('name', v)}     placeholder="Nombre completo" />
          <Field id="phone"    label="Teléfono"        value={form.phone}    onChange={(v: string) => set('phone', v)}    placeholder="(601) 123 4567" />
          <Field id="email"    label="Email"           value={form.email}    onChange={(v: string) => set('email', v)}    placeholder="correo@ejemplo.com" type="email" />
          <Field id="birthday" label="Cumpleaños"      value={form.birthday} onChange={(v: string) => set('birthday', v)} type="date" />

          {/* Campos DIAN */}
          <div className="border-t border-gray-100 dark:border-neutral-700 pt-3 mt-3 space-y-3">
            <h3 className="text-xs font-bold text-gray-500 uppercase">Datos Fiscales (DIAN)</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Tipo Documento</label>
                <select
                  value={form.identification_type}
                  onChange={e => setForm(p => ({ ...p, identification_type: e.target.value }))}
                  className="w-full rounded-xl border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/60"
                >
                  <option value="13">Cédula de Ciudadanía</option>
                  <option value="31">NIT</option>
                  <option value="22">Cédula de Extranjería</option>
                  <option value="41">Pasaporte</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Número Documento</label>
                <input
                  type="text"
                  value={form.identification_number}
                  onChange={e => setForm(p => ({ ...p, identification_number: e.target.value }))}
                  placeholder="Ej: 1023456789"
                  className="w-full rounded-xl border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/60"
                />
              </div>
            </div>

            {form.identification_type === '31' && (
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Dígito de Verificación (D.V.)</label>
                <input
                  type="text"
                  maxLength={1}
                  value={form.verification_digit}
                  onChange={e => setForm(p => ({ ...p, verification_digit: e.target.value }))}
                  placeholder="Ej: 5"
                  className="w-40 rounded-xl border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/60"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Tipo Organización</label>
                <select
                  value={form.organization_type}
                  onChange={e => setForm(p => ({ ...p, organization_type: e.target.value }))}
                  className="w-full rounded-xl border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/60"
                >
                  <option value="2">Persona Natural</option>
                  <option value="1">Persona Jurídica</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Responsabilidad Fiscal</label>
                <select
                  value={form.fiscal_responsibility}
                  onChange={e => setForm(p => ({ ...p, fiscal_responsibility: e.target.value }))}
                  className="w-full rounded-xl border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/60"
                >
                  <option value="R-99-PN">No responsable de IVA</option>
                  <option value="O-13">Responsable de IVA</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Dirección Fiscal</label>
              <input
                type="text"
                value={form.address}
                onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
                placeholder="Calle 80 # 24-12"
                className="w-full rounded-xl border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/60"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Ciudad</label>
                <input
                  type="text"
                  value={form.city_name}
                  onChange={e => setForm(p => ({ ...p, city_name: e.target.value }))}
                  placeholder="Ej: Bogotá"
                  className="w-full rounded-xl border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/60"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Código Divipola Ciudad</label>
                <input
                  type="text"
                  value={form.city_code}
                  onChange={e => setForm(p => ({ ...p, city_code: e.target.value }))}
                  placeholder="Ej: 11001"
                  className="w-full rounded-xl border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/60"
                />
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="notes" className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Notas internas</label>
            <textarea
              id="notes" value={form.notes} onChange={e => set('notes', e.target.value)} rows={2}
              placeholder="Preferencias, alergias, etc."
              className="w-full rounded-xl border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/60 resize-none"
            />
          </div>
        </div>
        <div className="flex gap-3 border-t border-gray-100 dark:border-neutral-700 px-5 py-4">
          <button onClick={onClose} className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-medium hover:bg-gray-50 transition">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 rounded-xl bg-primary py-2.5 text-sm font-bold text-white hover:opacity-90 disabled:opacity-60 transition">
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Customer Detail Panel ─────────────────────────────────────────────────────

function CustomerDetail({ id, onClose }: { id: number; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery<CustomerDetail>({
    queryKey: ['customer', id],
    queryFn: async () => { const { data } = await api.get(`/customers/${id}`); return data; },
  });

  const c = data?.customer;
  const tier = c ? TIER_META[c.tier] : null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-xl rounded-2xl bg-white dark:bg-neutral-800 shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-neutral-700 px-5 py-4 shrink-0">
          <h2 className="font-semibold text-gray-900 dark:text-white">Perfil del cliente</h2>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-neutral-700">
            <X size={16} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-5">
          {isLoading || !c ? (
            <div className="flex h-40 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="flex items-start gap-4">
                <div className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-2 text-2xl ${tier?.ring} ring-2`}>
                  {tier?.emoji}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">{c.name}</h3>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${tier?.color}`}>
                      <Award size={10} /> {tier?.label}
                    </span>
                    {c.phone && <span className="text-xs text-gray-500 flex items-center gap-1"><Phone size={10} />{c.phone}</span>}
                    {c.email && <span className="text-xs text-gray-500 flex items-center gap-1"><Mail size={10} />{c.email}</span>}
                    {c.birthday && <span className="text-xs text-gray-500 flex items-center gap-1"><Calendar size={10} />{fmtDate(c.birthday)}</span>}
                  </div>
                  {c.notes && <p className="mt-2 text-xs text-gray-500 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">{c.notes}</p>}
                </div>
              </div>

              {/* Datos Fiscales DIAN */}
              {(c.identification_number || c.address) && (
                <div className="rounded-xl border border-blue-100 dark:border-neutral-700 bg-blue-50/30 dark:bg-neutral-700/30 p-4 text-xs space-y-2">
                  <h4 className="font-bold text-blue-800 dark:text-blue-400 uppercase tracking-wide">Datos Fiscales (DIAN)</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-gray-500 block">Documento:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {c.identification_type === '31' ? 'NIT' : 'Cédula'} {c.identification_number}
                        {c.verification_digit ? `-${c.verification_digit}` : ''}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Organización:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {c.organization_type === '1' ? 'Persona Jurídica' : 'Persona Natural'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Régimen Fiscal:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {c.fiscal_responsibility === 'O-13' ? 'Responsable de IVA' : 'No responsable de IVA'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Dirección:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {c.address || '—'}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500 block">Ciudad:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {c.city_name || 'Bogotá'} ({c.city_code || '11001'})
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* KPIs */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Total gastado', value: fmt(c.total_spent), icon: TrendingUp },
                  { label: 'Puntos Fidelización', value: `🪙 ${c.loyalty_points ?? 0} pts`, icon: Award },
                  { label: 'Visitas',       value: String(c.visit_count), icon: ShoppingBag },
                ].map(({ label, value, icon: Icon }) => (
                  <div key={label} className="rounded-xl border border-gray-100 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-700/50 p-3 text-center">
                    <Icon size={16} className="mx-auto mb-1 text-primary" />
                    <p className="text-sm font-bold text-gray-900 dark:text-white">{value}</p>
                    <p className="text-[10px] text-gray-500">{label}</p>
                  </div>
                ))}
              </div>

              {/* Canje de Puntos */}
              <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-3.5">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-bold text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                    <Award size={14} /> Canje de Puntos de Fidelización
                  </span>
                  <span className="text-[11px] font-extrabold text-amber-600 dark:text-amber-300">
                    {c.loyalty_points ?? 0} pts disponibles
                  </span>
                </div>
                <p className="text-[11px] text-gray-500 mb-3">
                  Equivalencia: 100 puntos = $1.000 COP de descuento. Saldo actual equivalente:{' '}
                  <strong className="text-gray-700 dark:text-gray-300">
                    {fmt((c.loyalty_points ?? 0) * 10)}
                  </strong>
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="10"
                    step="10"
                    max={c.loyalty_points ?? 0}
                    placeholder="Puntos a canjear"
                    id={`redeem-input-${c.id}`}
                    className="w-full rounded-xl border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-1.5 text-xs text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      const inp = document.getElementById(`redeem-input-${c.id}`) as HTMLInputElement;
                      const pts = Number(inp?.value || 0);
                      if (!pts || pts <= 0) {
                        toast.error('Ingresa una cantidad de puntos válida');
                        return;
                      }
                      if (pts > (c.loyalty_points ?? 0)) {
                        toast.error('El cliente no tiene suficientes puntos');
                        return;
                      }
                      try {
                        const res = await api.post(`/customers/${c.id}/redeem-points`, { points: pts });
                        toast.success(res.data?.message || 'Puntos canjeados con éxito');
                        queryClient.invalidateQueries({ queryKey: ['customers'] });
                        queryClient.invalidateQueries({ queryKey: ['customer', id] });
                        if (inp) inp.value = '';
                      } catch (err: any) {
                        toast.error(err?.response?.data?.message || 'Error al canjear puntos');
                      }
                    }}
                    className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs px-3.5 py-1.5 whitespace-nowrap transition active:scale-95"
                  >
                    Canjear
                  </button>
                </div>
              </div>

              {/* Top products */}
              {data.top_products.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">Productos favoritos</h4>
                  <div className="space-y-1.5">
                    {data.top_products.map((p, i) => (
                      <div key={p.name} className="flex items-center justify-between text-sm">
                        <span className="text-gray-700 dark:text-gray-300">{i + 1}. {p.name}</span>
                        <span className="text-gray-500">{p.qty} uds.</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent orders */}
              {data.orders.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">Últimos pedidos</h4>
                  <div className="space-y-2">
                    {data.orders.slice(0, 6).map((o: any) => (
                      <div key={o.id} className="flex items-center justify-between rounded-xl border border-gray-100 dark:border-neutral-700 px-3 py-2 text-sm">
                        <div>
                          <span className="font-medium text-gray-900 dark:text-white">Orden #{o.id}</span>
                          <span className="ml-2 text-xs text-gray-500">{fmtDate(o.created_at)}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-semibold text-gray-900 dark:text-white">{fmt(o.total)}</span>
                          <span className={`ml-2 text-xs font-semibold ${o.payment_status === 'PAID' ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {o.payment_status === 'PAID' ? 'Pagado' : 'Pendiente'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function CustomersPage() {
  const queryClient = useQueryClient();
  const [search, setSearch]     = useState('');
  const [tierFilter, setTier]   = useState('');
  const [page, setPage]         = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState<Customer | null>(null);
  const [detail, setDetail]     = useState<number | null>(null);

  const { data: stats } = useQuery<GlobalStats>({
    queryKey: ['customers', 'stats'],
    queryFn: async () => {
      const { data } = await api.get('/customers/stats');
      // Normalizar la forma de la respuesta por seguridad
      return {
        totals: { total_customers: 0, total_revenue: 0, avg_spent: 0, total_visits: 0 },
        by_tier: [],
        new_this_month: 0,
        ...(data || {}),
      } as GlobalStats;
    },
    staleTime: 60_000,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['customers', 'list', search, tierFilter, page],
    queryFn: async () => {
      const { data } = await api.get('/customers', { params: { q: search, tier: tierFilter, page, per_page: 20 } });
      return data;
    },
    placeholderData: (prev: any) => prev,
  });

  const customers: Customer[] = data?.data ?? [];
  const total: number = data?.total ?? 0;
  const lastPage: number = data?.last_page ?? 1;

  const deleteMut = useMutation({
    mutationFn: (id: number) => api.delete(`/customers/${id}`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['customers'] }); toast.success('Cliente eliminado'); },
    onError: () => toast.error('Error al eliminar'),
  });

  const handleDelete = (c: Customer) => {
    if (!window.confirm(`¿Eliminar a ${c.name}? Sus órdenes quedarán sin vincular.`)) return;
    deleteMut.mutate(c.id);
  };

  const onSaved = () => { queryClient.invalidateQueries({ queryKey: ['customers'] }); };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-900 p-4 md:p-6">
      <div className="mx-auto max-w-6xl space-y-6">

        {/* ── Header ── */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Users size={24} className="text-primary" /> Clientes frecuentes
            </h1>
            <p className="text-sm text-gray-500">{total} clientes registrados</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => refetch()} className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 dark:border-neutral-600 hover:bg-gray-100 dark:hover:bg-neutral-700 transition">
              <RefreshCw size={15} />
            </button>
            <button
              onClick={() => { setEditing(null); setShowForm(true); }}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white hover:opacity-90 transition"
            >
              <Plus size={16} /> Nuevo cliente
            </button>
          </div>
        </div>

        {/* ── Global KPIs ── */}
        {stats && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[
              { label: 'Total clientes',    value: stats?.totals?.total_customers ?? 0, format: (v: number) => v.toString() },
              { label: 'Ingresos de clientes', value: stats?.totals?.total_revenue ?? 0,   format: fmt },
              { label: 'Gasto promedio',    value: stats?.totals?.avg_spent ?? 0,       format: fmt },
              { label: 'Nuevos este mes',   value: stats?.new_this_month ?? 0,         format: (v: number) => v.toString() },
            ].map(({ label, value, format }) => (
              <div key={label} className="rounded-2xl border border-gray-100 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-4 shadow-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
                <p className="mt-1 text-xl font-bold text-gray-900 dark:text-white">{format(value)}</p>
              </div>
            ))}
          </div>
        )}

        {/* ── Tier breakdown ── */}
        {stats && stats.by_tier.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setTier('')} className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition ${!tierFilter ? 'bg-primary text-white border-primary' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              Todos ({stats?.totals?.total_customers ?? 0})
            </button>
            {stats.by_tier.map(t => {
              const meta = TIER_META[t.tier as keyof typeof TIER_META];
              return (
                <button
                  key={t.tier}
                  onClick={() => setTier(t.tier === tierFilter ? '' : t.tier)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition ${tierFilter === t.tier ? `${meta.color} border-current` : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-neutral-600 dark:text-gray-400'}`}
                >
                  {meta.emoji} {meta.label} ({t.count})
                </button>
              );
            })}
          </div>
        )}

        {/* ── Search ── */}
        <div className="relative">
          <Search size={15} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nombre, teléfono o email…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full rounded-xl border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 pl-10 pr-4 py-2.5 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>

        {/* ── Table ── */}
        <div className="rounded-2xl border border-gray-100 dark:border-neutral-700 bg-white dark:bg-neutral-800 overflow-hidden shadow-sm">
          {isLoading ? (
            <div className="flex h-40 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : customers.length === 0 ? (
            <div className="flex h-40 flex-col items-center justify-center gap-2 text-gray-500">
              <Users size={32} strokeWidth={1} />
              <p className="text-sm">No hay clientes{search ? ' que coincidan' : ' registrados aún'}</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-gray-100 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-700/40">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-gray-500">Cliente</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-gray-500 hidden sm:table-cell">Contacto</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-gray-500">Nivel</th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wide text-gray-500">Puntos 🪙</th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wide text-gray-500">Gastado</th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wide text-gray-500 hidden md:table-cell">Visitas</th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wide text-gray-500 hidden lg:table-cell">Última visita</th>
                  <th className="px-4 py-3 w-20" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-neutral-700">
                {customers.map(c => {
                  const tier = TIER_META[c.tier];
                  return (
                    <tr
                      key={c.id}
                      className="hover:bg-gray-50 dark:hover:bg-neutral-700/30 cursor-pointer transition"
                      onClick={() => setDetail(c.id)}
                    >
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-900 dark:text-white">{c.name}</p>
                        {c.birthday && (
                          <p className="text-[11px] text-gray-400 flex items-center gap-1">
                            <Calendar size={9} /> {fmtDate(c.birthday)}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        {c.phone && <p className="text-gray-600 dark:text-gray-400 text-xs flex items-center gap-1"><Phone size={10}/>{c.phone}</p>}
                        {c.email && <p className="text-gray-500 text-xs flex items-center gap-1"><Mail size={10}/>{c.email}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${tier.color}`}>
                          {tier.emoji} {tier.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-bold text-amber-600 dark:text-amber-400">
                          🪙 {c.loyalty_points ?? 0} pts
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-white">{fmt(c.total_spent)}</td>
                      <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400 hidden md:table-cell">{c.visit_count}</td>
                      <td className="px-4 py-3 text-right text-gray-500 text-xs hidden lg:table-cell">{timeAgo(c.last_visit_at)}</td>
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => { setEditing(c); setShowForm(true); }} className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-600 text-gray-400 hover:text-primary transition">
                            <Edit2 size={14} />
                          </button>
                          <button onClick={() => handleDelete(c)} className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Pagination ── */}
        {lastPage > 1 && (
          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
            <span>{total} clientes · página {page} de {lastPage}</span>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition">
                <ChevronLeft size={14} />
              </button>
              <button onClick={() => setPage(p => Math.min(lastPage, p + 1))} disabled={page === lastPage} className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition">
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      {showForm && (
        <CustomerFormModal
          customer={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={onSaved}
        />
      )}
      {detail !== null && (
        <CustomerDetail id={detail} onClose={() => setDetail(null)} />
      )}
    </div>
  );
}
